import type { ReactNode } from 'react';

import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';

type WidgetRendererProps = {
  widgetType: string;
  dimensions: number[];
  layout: string;
};

type WidgetRenderer = (props: WidgetRendererProps) => ReactNode;

export type BaseWidgetProps = {
  widgetType: string;
  dimensions: number[];
  isLoading?: boolean;
  renderers?: Record<string, WidgetRenderer>;
  route?: string | null;
  compactTimeUpdate?: boolean;
} & {};

const BaseWidget = ({
  widgetType,
  dimensions,
  isLoading,
  renderers,
  route,
  compactTimeUpdate = false,
}: BaseWidgetProps): JSX.Element | null => {
  const renderer = renderers?.[widgetType];

  if (typeof renderer !== 'function') {
    return <div>Unknown widget type: {String(widgetType)}</div>;
  }

  if (isLoading) {
    return (
      <WidgetLoadingAnimation widgetName={widgetType} dimensions={dimensions} disableText={false} />
    );
  }

  if (Array.isArray(dimensions) === false || dimensions?.length !== 2) {
    console.error('The dimensions prop must be an array of two numbers.');
    return null;
  }

  const layout = `${dimensions[0]}x${dimensions[1]}`;

  return (
    <div className="d-flex flex-column h-100 w-100 min-h-0 overflow-hidden">
      {route ? <WidgetTimeUpdate route={route} realtime compact={compactTimeUpdate} /> : null}
      <div className="d-flex flex-column flex-fill h-100 min-h-0 overflow-hidden">
        {renderer({ widgetType, dimensions, layout })}
      </div>
    </div>
  );
};

export default BaseWidget;
